/**
 * Admin — Stories Management (standalone route).
 *
 * The actual management UI lives in <StoriesManager /> so it can be reused
 * as a tab inside the dashboard. This route keeps a thin auth gate + page
 * chrome for direct navigation to /admin/stories.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { BackToDashboard } from "@/components/rawan/BackToDashboard";
import { StoriesManager } from "@/components/rawan/StoriesManager";
import { useUserAuth } from "@/lib/user-auth";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/admin/stories")({
  head: () => ({
    meta: [
      { title: "إدارة قصص الويب — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminStoriesPage,
});

function AdminStoriesPage() {
  const { isAdmin: fbAdmin, loading: authLoading } = useUserAuth();
  const { isAuthed: localAdmin } = useAuth();
  const isAdmin = fbAdmin || localAdmin;
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !isAdmin) navigate({ to: "/login" });
  }, [authLoading, isAdmin, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-dvh grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!isAdmin) return null;

  return (
    <div className="min-h-dvh flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl" style={{ background: "var(--gradient-primary)" }}>
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="truncate text-lg sm:text-2xl font-extrabold">إدارة قصص الويب</h1>
            <p className="text-xs text-muted-foreground">قصص الويب AMP</p>
          </div>
          <BackToDashboard />
        </div>
        <StoriesManager />
      </main>
      <Footer />
    </div>
  );
}
